import * as React from 'react';
import {
  Modal,
  StackLayout,
  HeadingText,
  Button,
  InputStatusMessage,
  Spinner,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { FileUpload } from '../upload-asset-modal/file-upload.component';

type ChangeAssetModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectFile: (file: File) => void;
  enabled: boolean | undefined;
  error: boolean;
  setError: (value: boolean) => void;
  isLoading: boolean;
  accepts?: string;
  modalTitle: React.ReactNode;
};

export const ChangeAssetModal: React.FC<ChangeAssetModalProps> = ({
  open,
  onOpenChange,
  onSelectFile,
  enabled,
  error,
  setError,
  isLoading,
  accepts,
  modalTitle,
}) => {
  const { formatMessage } = useIntl();
  const [fileError, setFileError] = React.useState<Error | null>(null);
  // Track when a file has been selected to prevent flash of FileUpload
  // after upload completes but before modal closes
  const [hasSelectedFile, setHasSelectedFile] = React.useState(false);

  const handleSelectFile = React.useCallback(
    (file: File) => {
      setHasSelectedFile(true);
      onSelectFile(file);
    },
    [onSelectFile],
  );

  // Reset state when the modal opens to ensure a clean state
  React.useEffect(() => {
    if (open) {
      setHasSelectedFile(false);
      setFileError(null);
    }
  }, [open]);

  // Reset hasSelectedFile when an error occurs so the user can retry
  React.useEffect(() => {
    if (error) {
      setHasSelectedFile(false);
    }
  }, [error]);

  const showSpinner = isLoading || hasSelectedFile;

  return (
    <Modal.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setError(false);
          setFileError(null);
          setHasSelectedFile(false);
        }
        onOpenChange(next);
      }}
    >
      <Modal.Content>
        <StackLayout spacing="$one" horizontalAlignment="stretch">
          <Modal.Title>
            <HeadingText level="2" size="lg">
              {modalTitle}
            </HeadingText>
          </Modal.Title>
          {showSpinner ? (
            <StackLayout css={{ width: '100%' }} horizontalAlignment="center">
              <Spinner loading />
            </StackLayout>
          ) : (
            <FileUpload
              file={null}
              onSelect={handleSelectFile}
              onError={setFileError}
              disabled={!enabled}
              accepts={accepts}
            />
          )}
          {fileError ? (
            <InputStatusMessage inputStatus="error">
              {fileError.message}
            </InputStatusMessage>
          ) : null}
          {error && !fileError ? (
            <InputStatusMessage inputStatus="error">
              {formatMessage({ id: 'UPLOAD_FILE_ERROR' })}
            </InputStatusMessage>
          ) : null}
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="end"
            spacing="$half"
          >
            <Modal.Close>
              <Button
                priority="tertiary"
                quiet
                type="button"
                size="medium"
                disabled={isLoading}
              >
                {formatMessage({ id: 'CANCEL' })}
              </Button>
            </Modal.Close>
            <Button
              type="button"
              priority="primary"
              size="medium"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {formatMessage({ id: 'DONE' })}
            </Button>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
